/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-prototype-builtins */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Badge, Form } from "react-bootstrap";
import Button from "components/atoms/Button/Button";

import "./Filter.scss";
import { ReactComponent as FilterIcon } from "assets/images/icons/Filter.svg";
import { useGetFilterPresets } from "api/queries/user/useGetFilterPresets";
import { FilterConfig, FilterPreset, FilterPresetType } from "types/Common";
import errorToast from "helpers/errorToast";
import CreatePreset from "./components/CreatePreset";
import FilterAccordions from "./components/FilterAccordions";

type FilterProps = {
  config: Array<FilterConfig>;
  currentFilters: Record<string, any>;
  setFilters: Function;
  presetType?: FilterPresetType;
  eventContext: string;
};

function Filter(props: FilterProps) {
  const { config, currentFilters, setFilters, presetType, eventContext } = props;
  const [showFilter, setShowFilter] = useState(false);
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [initialRender, setInitialRender] = useState(true);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const filterIconRef = useRef<HTMLDivElement>(null);
  const selectedFilterCount = useMemo(() => {
    let count = 0;
    Object.keys(currentFilters).forEach((key) => {
      if (key === "query") return;
      if (Array.isArray(currentFilters[key]) && currentFilters[key].length) {
        count += 1;
      } else if (
        typeof currentFilters[key] === "object" &&
        currentFilters[key]?.hasOwnProperty("type") &&
        currentFilters[key].type
      ) {
        count += 1;
      } else if (typeof currentFilters[key] === "string" && currentFilters[key].length) {
        count += 1;
      }
    });
    return count;
  }, [currentFilters]);

  const getFiltersFromLocalStorage = () => {
    const localStorageFilters = localStorage.getItem(`${eventContext} Filters`);
    if (localStorageFilters) setFilters(JSON.parse(localStorageFilters));
  };

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
      return;
    }
    const { query, ...currentFiltersWithoutQuery } = currentFilters ?? {};
    if (!currentFiltersWithoutQuery) return;
    const localStorageFilters = JSON.stringify(currentFiltersWithoutQuery);
    localStorage.setItem(`${eventContext} Filters`, localStorageFilters);
  }, [currentFilters]);

  useEffect(() => {
    getFiltersFromLocalStorage();
    function handleOutsideClick(e: any) {
      if (!filterContainerRef.current?.contains(e.target)) {
        setShowFilter(false);
      }
    }
    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const { data: filterPresetsData } = useGetFilterPresets({
    refetchOnMount: false,
  });

  const presets: FilterPreset[] = Array.isArray(filterPresetsData)
    ? filterPresetsData.filter((item) => item.type === presetType)
    : [];

  const handleSavePresetClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (!Object.values(currentFilters)?.length) {
      errorToast(null, "Please select a filter to save");
      return;
    }
    setIsSaveMode(true);
  };

  const handlePresetSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPreset(e.target.value);
    if (!e.target.value) {
      setFilters({});
    }
    const presetData = presets.find((item) => item.label === e.target.value);
    if (presetData) {
      const data: Record<string, any> = {};
      presetData.presets.forEach((preset) => {
        data[preset.label] = preset.value;
      });
      setFilters(data);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSelectedPreset("");
  };

  return (
    <div
      ref={filterContainerRef}
      className="filter-container"
      onClick={(e: any) => {
        if (filterIconRef.current?.contains(e.target)) {
          setShowFilter((prev) => !prev);
        }
      }}
    >
      <div ref={filterIconRef}>
        <FilterIcon width={25} height={25} />
        {selectedFilterCount > 0 && (
          <div className="filter-badge">
            <Badge variant="primary" pill>
              {selectedFilterCount}
            </Badge>
          </div>
        )}
      </div>
      <div className={`filter-content ${showFilter ? "show" : ""}`}>
        {!isSaveMode ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>
                <FormattedMessage id="general.filters" defaultMessage="Filters" />
              </h5>
              {presetType && (
                <Button variant="outline-primary" onClick={handleSavePresetClick}>
                  <FormattedMessage id="general.savePreset" defaultMessage="Save Preset" />
                </Button>
              )}
            </div>
            <div className="filter-preset mb-1">
              {presetType && (
                <div className="select position-relative bg-white br-5 w-100">
                  <Form.Control
                    as="select"
                    className="pt-10 pb-10 pl-14 pr-14 height-auto form-control"
                    custom
                    value={selectedPreset}
                    onChange={handlePresetSelect}
                  >
                    <option value="">
                      <FormattedMessage id="general.selectPreset" defaultMessage="Select preset" />
                    </option>
                    {presets.map(({ label }) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </Form.Control>
                </div>
              )}
            </div>
            <hr className="mt-16 mb-24" />
            <FilterAccordions
              config={config}
              currentFilters={currentFilters}
              setFilters={setFilters}
              eventContext={eventContext}
            />
            
            <div className="w-100 d-flex justify-content-end">
              <Button variant="outline-primary" onClick={clearFilters}>
                <FormattedMessage id="general.clearAll" defaultMessage="Clear all" />
              </Button>
            </div>
          </>
        ) : (
          <CreatePreset
            eventContext={eventContext}
            setIsSaveMode={setIsSaveMode}
            currentFilters={currentFilters}
            presetType={presetType as FilterPresetType}
            presets={presets}
            setSelectedPreset={setSelectedPreset}
          />
        )}
      </div>
    </div>
  );
}

export default Filter;

Filter.defaultProps = {
  presetType: null,
};
